import { Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { FieldOption, FieldType, TemplateField } from '../../../types/FieldCommon';
import { handleSplitInput, isSelectionType } from '../../../utility/fieldUtils';
import FieldInput from '../FieldsSection/FieldInput';
import { FormFieldMetadata, typeOptions } from './templateFieldUtils';

interface ITemplateFieldModalProps<TField extends TemplateField> {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (field: TField) => void;
  fieldToEdit: TField | null;
  isForm?: boolean;
  createField?: () => TField;
}

const buildBlankField = <TField extends TemplateField>(createField?: () => TField): TField =>
  createField ? createField() : ({ label: '', type: FieldType.STRING, uuid: crypto.randomUUID(), value: null } as TField);

export default function TemplateFieldModal<TField extends TemplateField>({
  isOpen,
  onCancel,
  onSave,
  fieldToEdit,
  isForm = false,
  createField,
}: ITemplateFieldModalProps<TField>) {
  const [field, setField] = useState<TField>(() => buildBlankField(createField));
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setField(fieldToEdit ? { ...fieldToEdit } : buildBlankField(createField));
    setOptionInput('');
  }, [isOpen, fieldToEdit, createField]);

  const formField = field as TField & FormFieldMetadata;
  const isInvalid = _.isEmpty(_.trim(field.label));

  const setFieldElement = (key: string, value: unknown) => {
    setField((prev) => {
      const next = { ...prev, [key]: value } as TField & FormFieldMetadata;
      if (key === 'type' && !isSelectionType(value as FieldType)) {
        delete next.options;
      }
      return next;
    });
  };

  const commitOptionInput = () => {
    const trimmed = _.trim(optionInput);
    if (!trimmed) return;

    handleSplitInput(
      trimmed,
      field.options ?? [],
      (newOptions) => setFieldElement('options', newOptions),
      (option) => option
    );
    setOptionInput('');
  };

  const handleDeleteOption = (option: string) => {
    setFieldElement(
      'options',
      _.reject(field.options ?? [], (opt: string) => opt === option)
    );
  };

  const handleSubmit = () => {
    if (isInvalid) return;
    onSave(field);
    onCancel();
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography variant="h6" sx={{ m: 2 }}>
        {fieldToEdit ? 'Edit field' : 'Add field'}
      </Typography>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Label" value={field.label ?? ''} onChange={(event) => setFieldElement('label', event.target.value)} />

        <Autocomplete<FieldOption, false, true>
          disableClearable
          options={typeOptions}
          getOptionLabel={(option) => option.label}
          value={_.find(typeOptions, (option) => option.value === field.type) ?? typeOptions[0]}
          onChange={(_, fieldOption) => setFieldElement('type', fieldOption.value)}
          renderInput={(params) => <TextField {...params} label="Type" />}
        />

        {isForm && (
          <>
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Checkbox checked={Boolean(formField.required)} onChange={(event) => setFieldElement('required', event.target.checked)} />}
              label="Required"
            />

            <TextField
              label="Description"
              value={formField.description ?? ''}
              onChange={(event) => setFieldElement('description', event.target.value)}
              fullWidth
            />
          </>
        )}

        {isSelectionType(field.type) && (
          <Stack spacing={1}>
            <TextField
              label="Options"
              placeholder="Enter options separated by commas"
              value={optionInput}
              onChange={(event) => setOptionInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitOptionInput();
                }
              }}
              onBlur={commitOptionInput}
              fullWidth
            />

            {!_.isEmpty(field.options) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(field.options ?? []).map((option: string, index: number) => (
                  <Chip key={index} label={option} onDelete={() => handleDeleteOption(option)} />
                ))}
              </Box>
            )}
          </Stack>
        )}

        <Stack spacing={1}>
          <Typography variant="subtitle2">Default Value</Typography>

          <FieldInput field={field} updateFieldValue={(value) => setFieldElement('value', value)} />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isInvalid}>
          {fieldToEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
